import { sentenceCase } from "change-case";
import React, { useEffect, useRef, useState } from "react";
import { FieldValues, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { BlockButton } from "../../components/BlockButton/BlockButton";
import { Column } from "../../components/Column/Column";
import { Container } from "../../components/Container/Container";
import { Flex } from "../../components/Flex/Flex";
import { Form } from "../../components/Form/Form";
import { FormField, FormFieldOptions } from "../../components/GeneratedForm/GeneratedForm";
import { P } from "../../components/Paragraph/Paragraph";
import { Title } from "../../components/Title/Title";
import { toastState, ToastType } from "../../components/Toast/Toast";
import { View } from "../../components/View/View";
import { adminCreatableFeedItems } from "../../constants";
import { AdminViewParams, ADMIN_VIEW_PATH } from "../../routes";
import {
  AdminFeedItemAudienceEnum,
  AdminFeedItemTypeEnum,
  AdminFeedTopic,
  useAdminFeedTopicsQuery,
  useCreateAdminFeedItemMutation,
} from "../../schema";
import { buildUrl } from "../../services/buildUrl";
import formOptions from "../../services/getEnumFormOptions";
import { getMetadataFromFields, MetadataFormFields } from "../../services/getMetadataFromFields";
import { getMetadataFormFields } from "../../services/getMetadataSchema";
import { validateMaximumLength } from "../../validators/validateMaximumLength";
import { validateNumeric } from "../../validators/validateNumeric";
import { AdminViewProps } from "../AdminView/AdminView";
import { ErrorView } from "../ErrorView/ErrorView";
import { LoadingView } from "../LoadingView/LoadingView";

interface CreateAdminFeedItemData extends FieldValues {
  type: AdminFeedItemTypeEnum;
  audience: AdminFeedItemAudienceEnum[];
  isPinned: "true" | "false";
  isRecommended: "true" | "false";
  metadata: MetadataFormFields;
  startDate: string | null;
  endDate: string | null;
  startDay: string;
  endDay: string;
  likeCount: string;
  shareCount: string;
  version: string;
  topicIds: string[];
  description: string;
}

const defaultType = adminCreatableFeedItems[0];

export const AdminFeedItemCreateView: React.FC<AdminViewProps> = () => {
  const navigate = useNavigate();
  const setToast = useSetRecoilState(toastState);
  const formRef = useRef<HTMLFormElement>(null);
  const [metadataFormFields, setMetadataFormFields] = useState<FormFieldOptions[]>([]);

  const formMethods = useForm<CreateAdminFeedItemData>({
    defaultValues: {
      metadata: metadataFormFields,
    },
  });

  const { data, loading, error } = useAdminFeedTopicsQuery();

  const { handleSubmit, watch, resetField, clearErrors } = formMethods;

  const feedItemType = watch("type");

  useEffect(() => {
    // update metadata part of the form, if feedItemType is undefined we set initial type to default type
    const newFields = getMetadataFormFields(feedItemType || defaultType);

    setMetadataFormFields(newFields);
    resetField("metadata");
    clearErrors();
  }, [feedItemType, resetField, clearErrors]);

  const [createAdminFeedItem] = useCreateAdminFeedItemMutation({
    onCompleted: (data) => {
      // show success toast message
      setToast({
        type: ToastType.SUCCESS,
        title: "Created a new admin feed item",
        message: `Admin feed item of type "${sentenceCase(data.createAdminFeedItem.type)}" was created`,
        isOpen: true,
      });

      // navigate to item details view
      navigate({
        pathname: buildUrl<AdminViewParams>(ADMIN_VIEW_PATH, {
          menu: "admin-feed",
          page: "items",
          id: data.createAdminFeedItem.id,
        }),
      });
    },
  });

  const onSubmit: SubmitHandler<CreateAdminFeedItemData> = (data) => {
    createAdminFeedItem({
      variables: {
        ...data,
        isPinned: data.isPinned === "true",
        isRecommended: data.isRecommended === "true",
        startDay: data.startDay ? parseInt(data.startDay, 10) : null,
        endDay: data.endDay ? parseInt(data.endDay, 10) : null,
        likeCount: data.likeCount ? parseInt(data.likeCount, 10) : null,
        shareCount: data.shareCount ? parseInt(data.shareCount, 10) : null,
        version: data.version ? parseInt(data.version, 10) : null,
        topicIds: data.topicIds,
        metadata: getMetadataFromFields(data.metadata, feedItemType),
        description: data.description || "",
      },
    });
  };

  if (loading) {
    return <LoadingView />;
  }

  // handle errors
  if (error || !data?.adminFeedTopics || data?.adminFeedTopics.length === 0) {
    return (
      <ErrorView
        title="Fetching admin feed topics failed"
        error={error || "No topics were found for Admin feed items"}
      />
    );
  }

  return (
    <View pad="half" scrollable>
      <Title>Create new feed item</Title>
      <FormProvider {...formMethods}>
        <Form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <Flex overflow row={false} expanded={false}>
            {getFormFields(data?.adminFeedTopics).map((field) => (
              <Container overflow key={field.name} expanded>
                <FormField field={field} />
              </Container>
            ))}
            <h3>Metadata</h3>

            {metadataFormFields.length === 0 && <P>No metadata required</P>}

            {metadataFormFields.map((field) => {
              return (
                <Container overflow key={field.name} expanded>
                  <FormField field={field} />
                </Container>
              );
            })}
          </Flex>

          <Column marginTop="half" crossAxisAlignment="flex-end">
            <BlockButton inline tertiary loading={false} type="submit">
              Create new item
            </BlockButton>
          </Column>
        </Form>
      </FormProvider>
    </View>
  );
};

const getFormFields = (adminFeedTopics: AdminFeedTopic[]) => {
  return [
    {
      field: "text",
      type: "text",
      name: "description",
      label: "Description of the feed item",
      rules: {
        validate: validateMaximumLength(255),
      },
    },
    {
      field: "radio",
      name: "type",
      label: "Item type",
      options: formOptions.fromArray(adminCreatableFeedItems),
      rules: { required: "Please choose type" },
      defaultValue: defaultType,
    },
    {
      field: "checkbox",
      name: "audience",
      label: "Audience who will see this item",
      options: formOptions.fromEnum(AdminFeedItemAudienceEnum),
      rules: { required: "Please choose at least one audience type" },
    },
    {
      field: "radio",
      name: "isPinned",
      label: "Is pinned to the top",
      options: [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
      rules: { required: "Please choose whether the item is pinned to the top" },
      defaultValue: "false",
    },
    {
      field: "radio",
      name: "isRecommended",
      label: "Is recommended feed item",
      options: [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
      rules: { required: "Please choose whether the item is recommended" },
      defaultValue: "false",
    },
    {
      field: "date",
      name: "startDate",
      label: "Show item from given date",
      defaultValue: new Date(),
    },
    {
      field: "date",
      name: "endDate",
      label: "Show item until given date (leave empty to not use)",
    },
    {
      field: "text",
      type: "text",
      name: "startDay",
      label: "Show item starting from given days after registration (0 for first day, empty to not use)",
      rules: {
        validate: validateNumeric({
          min: 0,
          optional: true,
          message: "Expected positive start day number",
        }),
      },
    },
    {
      field: "text",
      type: "text",
      name: "endDay",
      label: "Show item starting until given days after registration (leave empty to not use)",
      rules: {
        validate: validateNumeric({
          min: 0,
          optional: true,
          message: "Expected positive end day number",
        }),
      },
    },
    {
      field: "text",
      type: "number",
      name: "likeCount",
      label: "Initial like count",
      rules: {
        validate: validateNumeric({
          min: 0,
          optional: true,
          message: "Expected positive like count",
        }),
      },
      defaultValue: "0",
    },
    {
      field: "text",
      type: "number",
      name: "shareCount",
      label: "Initial share count",
      rules: {
        validate: validateNumeric({
          min: 0,
          optional: true,
          message: "Expected positive share count",
        }),
      },
      defaultValue: "0",
    },
    {
      field: "text",
      type: "text",
      name: "version",
      label: "Metadata version",
      rules: { required: "Please provide metadata version" },
      defaultValue: "1",
    },
    {
      field: "checkbox",
      name: "topicIds",
      label: "Admin feed item topics",
      options: adminFeedTopics.map((topic) => ({ value: topic.id, label: topic.name })),
      rules: { required: "Please choose at least one topic" },
      defaultValue: adminFeedTopics.filter((topic) => topic.isPermanent).map((topic) => topic.id),
    },
  ] as FormFieldOptions[];
};
